// The image-model showcase on screen (MS-IMG): every picture model on the same prompts.
//   ImageShowcaseSection  the Models screen's Images tab: desktop a grid (style rows x model columns), phone a style
//                         at a time with the models to swipe. Tap a still to see it large.
//   StillStrip            one model's stills as a thumbnail strip (the relevant style first), with its price per
//                         picture: on the Images tab's cards, in the image pickers and the compare sheet.
//   StillViewer           a still large, with its model, style and price; arrows step through the strip.
//   StillCompare          2-4 models' stills of one row side by side (the stage: show_showcase step frame).
// Everything renders nothing until the showcase has stills (GET /api/model-showcase/images).
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { compareStills, creditsWords, hasStills, priceWordsOf, refsCostMore, stillCredits, loadImageShowcase, pickRow, stillModelFor, stillOf, stillsFor, type ImageShowcaseData, type StillModel, type StillRow } from "../showcaseImages";
import { Icon } from "./bits";
import { useWide } from "./ShowcaseGrid";

/** The image showcase, loaded once per page (cached in showcaseImages.ts); null while loading or when nothing's made. */
export function useImageShowcase(): ImageShowcaseData | null {
  const [data, setData] = useState<ImageShowcaseData | null>(null);
  useEffect(() => {
    let on = true;
    void loadImageShowcase().then((d) => on && setData(hasStills(d) ? d : null));
    return () => {
      on = false;
    };
  }, []);
  return data;
}

type Shown = { model: StillModel; items: { imageUrl: string; row: StillRow }[]; at: number };

// ------------------------------------------------------------------ the large view

export function StillViewer({ shown, onClose }: { shown: Shown; onClose: () => void }) {
  const [at, setAt] = useState(shown.at);
  const it = shown.items[at]!;
  const n = shown.items.length;
  const go = useCallback((d: number) => setAt((i) => (i + d + n) % n), [n]);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    box.current?.focus({ preventScroll: true });
    // capture: Escape closes this, not a sheet underneath
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      } else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", key, true);
    return () => {
      window.removeEventListener("keydown", key, true);
      prev?.focus?.();
    };
  }, [onClose, go]);
  return createPortal(
    <div className="stv-scrim" onClick={onClose}>
      <div className="stv" role="dialog" aria-modal="true" aria-label={`${shown.model.short}: ${it.row.name}`} tabIndex={-1} ref={box} onClick={(e) => e.stopPropagation()}>
        <header className="stv-head">
          <span className="stv-title">
            <b>{shown.model.short}</b>
            <span>{it.row.name}</span>
          </span>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">
            <Icon name="close" size={20} />
          </button>
        </header>
        <img className="stv-img" src={it.imageUrl} alt={`${shown.model.short}'s picture for “${it.row.name}”`} />
        <p className="stv-watch">{it.row.watch}</p>
        <footer className="stv-foot">
          <span className="stv-price">
            {creditsWords(stillCredits(shown.model, it.row.kind))}
            {shown.model.rateSource === "unverified" && <span className="msc-est">estimate</span>}
          </span>
          {n > 1 && (
            <span className="stv-nav">
              <button type="button" className="icon-btn" onClick={() => go(-1)} aria-label="Previous style">
                <Icon name="back" size={18} />
              </button>
              <span className="muted" aria-live="polite">
                {at + 1} of {n}
              </span>
              <button type="button" className="icon-btn msc-next" onClick={() => go(1)} aria-label="Next style">
                <Icon name="back" size={18} />
              </button>
            </span>
          )}
        </footer>
        <p className="stv-note muted">Same prompt on every model, first picture, nothing picked.</p>
      </div>
    </div>,
    document.body,
  );
}

// ------------------------------------------------------------------ one model's strip

/**
 * One model's stills, the relevant row first. `model` is the catalog id (and label, so the other route of the same
 * model, like Nano Banana Pro on fal, still finds its stills). Renders nothing when the model has none.
 */
export function StillStrip({ data, modelId, label, row, max = 6, price = true }: { data: ImageShowcaseData; modelId: string; label?: string; row?: string | null; max?: number; price?: boolean }) {
  const [open, setOpen] = useState<number | null>(null);
  const m = stillModelFor(data, modelId, label);
  if (!m) return null;
  const items = stillsFor(data, m.id, row).slice(0, max);
  if (!items.length) return null;
  return (
    <div className="sts">
      <ul className="sts-row" aria-label={`${m.short}'s showcase pictures`}>
        {items.map((c, i) => (
          <li key={c.rowId}>
            <button type="button" className={`sts-thumb${i === 0 && row && c.rowId === row ? " is-first" : ""}`} onClick={() => setOpen(i)} aria-label={`See ${m.short}'s ${c.row.name} picture large`} title={c.row.name}>
              <img src={c.thumbUrl} alt="" loading="lazy" decoding="async" />
            </button>
          </li>
        ))}
      </ul>
      {price && <span className="sts-price">{priceWordsOf(m)}</span>}
      {open !== null && <StillViewer shown={{ model: m, items: items.map((c) => ({ imageUrl: c.imageUrl, row: c.row })), at: open }} onClose={() => setOpen(null)} />}
    </div>
  );
}

// ------------------------------------------------------------------ the gallery (Models › Images)

export function ImageShowcaseSection() {
  const data = useImageShowcase();
  if (!data) return null;
  return <ImageShowcaseGrid data={data} />;
}

export function ImageShowcaseGrid({ data }: { data: ImageShowcaseData }) {
  const wide = useWide();
  const [rowId, setRowId] = useState<string | null>(null);
  const [open, setOpen] = useState<Shown | null>(null);
  const row = pickRow(data, rowId);
  const view = (m: StillModel, r: StillRow) => {
    const items = data.rows.flatMap((x) => {
      const c = stillOf(data, x.id, m.id);
      return c ? [{ imageUrl: c.imageUrl, row: x }] : [];
    });
    setOpen({ model: m, items, at: Math.max(0, items.findIndex((x) => x.row.id === r.id)) });
  };
  return (
    <section className="msc isc" aria-labelledby="isc-h">
      <header className="msc-head">
        <h2 id="isc-h" className="section-title">
          Same prompt, every picture model
        </h2>
        <p className="muted">{data.note}</p>
      </header>
      {wide ? (
        <div className="msc-table" role="table" aria-label="Every style on every picture model" style={{ ["--cols" as string]: data.models.length }}>
          <div className="msc-row msc-row-head" role="row">
            <span className="msc-corner" role="columnheader">
              Style
            </span>
            {data.models.map((m) => (
              <span key={m.id} className="msc-mhead" role="columnheader" title={m.label}>
                <b>{m.short}</b>
                <span className="msc-price">
                  {m.credits} credits <span>a picture</span>
                  {refsCostMore(m) && (
                    <span className="msc-refprice">
                      {m.refCredits} <span>with references</span>
                    </span>
                  )}
                </span>
                <span className="msc-badges">{m.refs && <span className="tag">Takes references</span>}</span>
              </span>
            ))}
          </div>
          {data.rows.map((r) => (
            <div key={r.id} className={`msc-row${r.kind === "ref" ? " is-ref" : ""}`} role="row">
              <span className="msc-style" role="rowheader" title={r.watch}>
                <span className="msc-n">{String(r.n).padStart(2, "0")}</span>
                <b>{r.name}</b>
              </span>
              {data.models.map((m) => {
                const c = stillOf(data, r.id, m.id);
                return (
                  <span key={m.id} role="cell" className="msc-cell">
                    {c ? (
                      <button type="button" className="msc-clip isc-still" onClick={() => view(m, r)} aria-label={`See ${m.short}: ${r.name}`}>
                        <img src={c.thumbUrl} alt="" loading="lazy" decoding="async" />
                      </button>
                    ) : (
                      <div className="msc-clip is-empty">{r.kind === "ref" && !m.refs ? "Words only" : "Not made yet"}</div>
                    )}
                  </span>
                );
              })}
            </div>
          ))}
        </div>
      ) : (
        row && (
          <div className="msc-phone">
            <div className="msc-styles" role="tablist" aria-label="Styles">
              {data.rows.map((r) => (
                <button key={r.id} type="button" role="tab" aria-selected={r.id === row.id} className={`chip${r.id === row.id ? " is-on" : ""}`} onClick={() => setRowId(r.id)}>
                  {r.name}
                </button>
              ))}
            </div>
            <p className="msc-watch">{row.watch}</p>
            <ul className="isc-cards" aria-label={`${row.name} on every picture model`}>
              {data.models.map((m) => {
                const c = stillOf(data, row.id, m.id);
                if (!c) return null;
                return (
                  <li key={m.id} className="isc-card">
                    <button type="button" className="msc-clip isc-still" onClick={() => view(m, row)} aria-label={`See ${m.short}: ${row.name}`}>
                      <img src={c.thumbUrl} alt="" loading="lazy" decoding="async" />
                    </button>
                    <span className="isc-cap">
                      <b>{m.short}</b>
                      <span className="msc-price">
                        {stillCredits(m, row.kind)} <span>credits</span>
                      </span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )
      )}
      {open && <StillViewer shown={open} onClose={() => setOpen(null)} />}
    </section>
  );
}

// ------------------------------------------------------------------ side by side (the stage)

/** 2-4 picture models' stills of one row, side by side; tap one to see it large (then step through the others). */
export function StillCompare({ data, rowId, models }: { data: ImageShowcaseData; rowId: string; models: string[] }) {
  const [open, setOpen] = useState<number | null>(null);
  const row = data.rows.find((r) => r.id === rowId);
  const shown = compareStills(data, rowId, models);
  if (!row || shown.length < 2) return null;
  const ms = shown.map((c) => data.models.find((m) => m.id === c.key)!);
  return (
    <section className={`scmp scmp-${shown.length}`} aria-label={`${row.name}, side by side`}>
      <header className="scmp-head">
        <h3 className="scmp-title">{row.name}</h3>
      </header>
      <div className="scmp-grid">
        {shown.map((c, i) => (
          <figure key={c.key} className="scmp-clip">
            <button type="button" className="scmp-frame" onClick={() => setOpen(i)} aria-label={`See ${c.title}'s picture large`}>
              <img className="scmp-img" src={c.thumbUrl} alt="" />
            </button>
            <figcaption>
              <b>{c.title}</b>
              <span>{c.detail}</span>
            </figcaption>
          </figure>
        ))}
      </div>
      <p className="muted scmp-note">{row.watch}</p>
      {open !== null && <StillViewer shown={{ model: ms[open]!, items: [{ imageUrl: shown[open]!.imageUrl, row }], at: 0 }} onClose={() => setOpen(null)} />}
    </section>
  );
}
